import { getOrCreateSessionId } from "./triageJobsStorage";

const truncate = (value, max) => {
  const text = String(value ?? "").trim();
  if (!text) return undefined;
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
};

const parseBrowserOs = (ua) => {
  const source = ua || "";
  let browser = "Unknown";
  if (/Edg\//i.test(source)) browser = "Edge";
  else if (/OPR\/|Opera/i.test(source)) browser = "Opera";
  else if (/Chrome\//i.test(source) && !/Chromium/i.test(source)) browser = "Chrome";
  else if (/Safari\//i.test(source) && !/Chrome\//i.test(source)) browser = "Safari";
  else if (/Firefox\//i.test(source)) browser = "Firefox";

  let os = "Unknown";
  if (/Windows NT/i.test(source)) os = "Windows";
  else if (/Mac OS X|Macintosh/i.test(source)) os = "macOS";
  else if (/Android/i.test(source)) os = "Android";
  else if (/iPhone|iPad|iPod/i.test(source)) os = "iOS";
  else if (/Linux/i.test(source)) os = "Linux";
  else if (/CrOS/i.test(source)) os = "ChromeOS";

  return { browser, os };
};

const classifyDevice = (width, ua) => {
  if (/Mobi|Android.*Mobile|iPhone|iPod/i.test(ua || "")) return "Mobile";
  if (/iPad|Tablet|Android(?!.*Mobile)/i.test(ua || "")) return "Tablet";
  if (typeof width === "number") {
    if (width < 768) return "Mobile";
    if (width < 1024) return "Tablet";
  }
  return "Desktop";
};

const readUtm = () => {
  if (typeof window === "undefined") return undefined;
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
    const parts = keys
      .map((key) => {
        const value = params.get(key);
        return value ? `${key.replace("utm_", "")}=${value}` : null;
      })
      .filter(Boolean);
    return parts.length ? parts.join(" · ") : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Collect non-PII client environment for Discord activity embeds.
 * Safe in SSR / non-browser contexts (returns {}).
 */
export const collectClientContext = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {};
  }

  const ua = navigator.userAgent || "";
  const { browser, os } = parseBrowserOs(ua);
  const viewportW = window.innerWidth || undefined;
  const viewportH = window.innerHeight || undefined;
  const screenW = window.screen?.width || undefined;
  const screenH = window.screen?.height || undefined;
  const dpr = window.devicePixelRatio || undefined;
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  let timezone;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timezone = undefined;
  }

  let colorScheme;
  try {
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) colorScheme = "dark";
    else if (window.matchMedia("(prefers-color-scheme: light)").matches) colorScheme = "light";
  } catch {
    colorScheme = undefined;
  }

  const context = {
    sessionId: truncate(getOrCreateSessionId(), 64),
    device: classifyDevice(viewportW, ua),
    browser,
    os,
    viewport: viewportW && viewportH ? `${viewportW}×${viewportH}` : undefined,
    screen: screenW && screenH ? `${screenW}×${screenH}` : undefined,
    dpr: typeof dpr === "number" ? Number(dpr.toFixed(2)) : undefined,
    language: truncate(navigator.language || navigator.languages?.[0], 32),
    timezone: truncate(timezone, 64),
    referrer: truncate(document.referrer || "direct", 200),
    pageUrl: truncate(window.location.href, 300),
    touch: Boolean(navigator.maxTouchPoints > 0 || "ontouchstart" in window),
    connection: truncate(connection?.effectiveType, 32),
    colorScheme,
    utm: truncate(readUtm(), 200),
    deployment: truncate(process.env.REACT_APP_DEPLOYMENT, 32),
  };

  // Drop undefined keys so Zod + Discord stay clean.
  return Object.fromEntries(Object.entries(context).filter(([, value]) => value !== undefined));
};
